import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts } from "@/lib/shopify";
import { ProductCard } from "@/components/site/ProductCard";
import { EmptyProducts } from "@/components/site/EmptyProducts";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Scissors, HandHeart, PackageOpen } from "lucide-react";
import heroImg from "@/assets/hero.jpg";
import standardImg from "@/assets/standard.jpg";
import audienceAlopecia from "@/assets/audience-alopecia.jpg";
import audienceChemo from "@/assets/audience-chemo.jpg";
import audienceReligious from "@/assets/audience-religious.jpg";
import audienceFashion from "@/assets/audience-fashion.jpg";
import packagingImg from "@/assets/packaging.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://europeanwigluxury.com/" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://europeanwigluxury.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" },
    ],
  }),
  component: Home,
});

const audiences = [
  { key: "alopecia", title: "Alopecia & thinning hair", copy: "Breathable, lightweight caps designed for daily comfort against sensitive scalps.", image: audienceAlopecia },
  { key: "chemo", title: "Chemotherapy & medical hair loss", copy: "Soft, gentle hair on hand-tied caps — designed to feel weightless during treatment.", image: audienceChemo },
  { key: "religious", title: "Religious & modest wear", copy: "Realistic hairlines and natural parts, crafted for everyday coverage that reads true.", image: audienceReligious },
  { key: "fashion", title: "Fashion & everyday luxury", copy: "Editorial-worthy shapes and shades, styled without the commitment of your own length.", image: audienceFashion },
] as const;

const standard = [
  { icon: HandHeart, title: "Virgin European hair", body: "Every piece is made from 100% virgin European hair — sourced for its fine-to-medium density and natural blonde-to-brunette range, and left in the state it grew in." },
  { icon: ShieldCheck, title: "100% virgin, unprocessed", body: "No dye, no bleach, no perm, no acid bath. The hair reaches us in its natural state and keeps its natural strength, shine, and lifespan." },
  { icon: Scissors, title: "Cuticle-aligned root to tip", body: "Every strand runs in the same direction. Cuticles lie flat together, so the hair resists tangling and reflects light like it grew from the scalp." },
  { icon: PackageOpen, title: "Hand-tied cap construction", body: "Each hair is individually knotted by hand into a soft cap. Movement, parting, and comfort you can't get from a machine-wefted wig." },
];

const testimonials = [
  { quote: "It moves like hair I've always known. Nobody has once asked if I'm wearing it — even people who knew before treatment.", who: "Wearer since 2023" },
  { quote: "I've bought three now. The construction is exactly what I need for the way I cover — modest, but never obviously a wig.", who: "Modest wear client" },
  { quote: "I came in for a fashion piece and stayed because of the fit. It feels like a real haircut, not a costume.", who: "Editorial client" },
  { quote: "After alopecia I stopped photographing my kids. This is the first piece I've owned that made me forget I was wearing one.", who: "Alopecia client" },
];

function Home() {
  const { data: products } = useQuery({
    queryKey: ["products", "featured"],
    queryFn: () => fetchProducts("product_type:Wig"),
  });
  const featured = products?.slice(0, 4) ?? [];

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-secondary/40">
        <div className="container-luxe grid gap-10 py-16 md:grid-cols-12 md:gap-16 md:py-24">
          <div className="flex flex-col justify-center md:col-span-6">
            <p className="eyebrow">The European Wig Luxury Standard</p>
            <h1 className="mt-4 font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
              Premium Virgin European Hair.<br />
              <span className="italic text-bronze">Nothing else.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              Short-length wigs made from 100% virgin European hair — unprocessed,
              cuticle-aligned root to tip, and hand-tied strand by strand into a soft, breathable cap.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button asChild size="lg" className="h-12 w-full rounded-full px-7 sm:w-auto">
                <Link to="/shop">Shop wigs <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 w-full rounded-full px-7 sm:w-auto">
                <Link to="/contact">Ask us a question</Link>
              </Button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">$500–$1,000 · Discreet packaging · Cranial prosthesis invoice available.</p>
          </div>
          <div className="relative md:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden rounded-md bg-muted shadow-[0_30px_80px_-40px_rgba(60,40,20,0.35)]">
              <img src={heroImg} alt="A woman wearing a short brown virgin European hair wig" width={1600} height={1808} fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden max-w-[220px] rounded-md border border-border bg-background/95 p-5 shadow-lg backdrop-blur md:block">
              <p className="eyebrow">100% Virgin</p>
              <p className="mt-2 font-display text-base leading-snug">Unprocessed European hair. Cuticles aligned root to tip.</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE STANDARD */}
      <section className="border-t border-border/70">
        <div className="container-luxe grid gap-14 py-20 md:grid-cols-12 md:py-28">
          <div className="md:col-span-5">
            <p className="eyebrow">Our craft</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl">The European Wig Luxury Standard</h2>
            <p className="mt-5 text-muted-foreground">
              We don't use grade numbers. The industry's 9A / 10A / 12A labels are unregulated marketing.
              What actually makes a wig worth its price is four specific things — and we hold every wig to all four.
            </p>
            <div className="mt-8 aspect-[4/3] overflow-hidden rounded-md">
              <img src={standardImg} alt="Detail of cuticle-aligned virgin European hair" loading="lazy" width={1400} height={1000} className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="grid gap-6 md:col-span-7 md:grid-cols-2">
            {standard.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-md border border-border/70 bg-card p-7">
                <Icon className="h-6 w-6 text-bronze" strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-xl">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP BY NEED */}
      <section className="bg-secondary/40">
        <div className="container-luxe py-20 md:py-28">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="eyebrow">Shop by need</p>
              <h2 className="mt-3 font-display text-3xl md:text-5xl">One standard. Every reason.</h2>
            </div>
            <p className="max-w-md text-sm text-muted-foreground">
              Whether you're covering medical hair loss, honoring a religious practice, or simply choosing
              a new shape — the same wig, and the same care, is behind every purchase.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {audiences.map((a) => (
              <Link
                key={a.key}
                to="/shop"
                search={{ audience: a.key }}
                className="group flex flex-col overflow-hidden rounded-md bg-background transition-shadow hover:shadow-lg"
              >
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  <img src={a.image} alt={a.title} loading="lazy" width={900} height={1100} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-xl leading-tight">{a.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.copy}</p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-bronze">
                    Shop <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="container-luxe py-20 md:py-28">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">The collection</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl">Short-length, hand-tied</h2>
          </div>
          <Link to="/shop" className="hidden text-sm font-medium text-bronze md:inline-flex">View all →</Link>
        </div>
        {featured.length ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p) => <ProductCard key={p.node.id} product={p} />)}
          </div>
        ) : (
          <EmptyProducts />
        )}
      </section>

      {/* TRUST ROW */}
      <section className="border-y border-border/70 bg-background">
        <div className="container-luxe grid gap-8 py-12 md:grid-cols-4 md:py-14">
          {[
            { t: "100% virgin European hair", d: "Unprocessed. Never dyed or bleached." },
            { t: "Hand-tied craftsmanship", d: "Knotted strand by strand." },
            { t: "Discreet packaging", d: "Unmarked outer box." },
            { t: "Insurance invoice available", d: "Cranial prosthesis billing for reimbursement." },
          ].map((c) => (
            <div key={c.t}>
              <p className="font-display text-lg">{c.t}</p>
              <p className="mt-1 text-sm text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="container-luxe py-20 md:py-28">
        <p className="eyebrow">In their words</p>
        <h2 className="mt-3 max-w-2xl font-display text-3xl md:text-5xl">Worn quietly. Recommended loudly.</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t) => (
            <figure key={t.who} className="flex h-full flex-col rounded-md border border-border/70 bg-card p-7">
              <blockquote className="flex-1 font-display text-lg leading-snug">"{t.quote}"</blockquote>
              <figcaption className="mt-6 text-xs uppercase tracking-widest text-muted-foreground">{t.who}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CARE & CONSULT */}
      <section className="bg-secondary/40">
        <div className="container-luxe grid gap-12 py-20 md:grid-cols-2 md:py-28">
          <div className="relative aspect-[4/3] overflow-hidden rounded-md">
            <img src={packagingImg} alt="Unbranded European Wig Luxury packaging" loading="lazy" width={1400} height={900} className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col justify-center">
            <p className="eyebrow">Care & questions</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl">We stay with the piece.</h2>
            <p className="mt-5 text-muted-foreground">
              A wig this considered deserves considered care. Send us a question before you buy,
              or read our care guide for washing, styling, and storage that preserves virgin hair for years.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-12 rounded-full px-7"><Link to="/contact">Contact us</Link></Button>
              <Button asChild variant="outline" size="lg" className="h-12 rounded-full px-7"><Link to="/care">Read the care guide</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
